// Pure signal-math helpers. No DOM access here so they can be unit-tested.
// The SDK only ever sends AGGREGATES computed by these functions - never raw
// keystrokes, never PII.

export interface MouseSummary {
  moves: number;
  path_entropy: number;
  distance: number;
}

export interface TypingSummary {
  keys: number;
  flight_var: number;
  dwell_mean: number;
}

export function variance(xs: number[]): number {
  if (xs.length < 2) return 0;
  const mean = xs.reduce((a, b) => a + b, 0) / xs.length;
  const ss = xs.reduce((a, b) => a + (b - mean) * (b - mean), 0);
  return ss / xs.length;
}

export function mean(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

// Shannon entropy of the distribution of movement directions, normalized to
// [0,1]. Straight-line / bot-like motion is low; human jitter is high.
export function directionEntropy(angles: number[]): number {
  if (angles.length === 0) return 0;
  const bins = new Array(8).fill(0);
  for (const a of angles) {
    const idx = Math.min(7, Math.max(0, Math.floor(((a + Math.PI) / (2 * Math.PI)) * 8)));
    bins[idx] += 1;
  }
  const total = angles.length;
  let h = 0;
  for (const c of bins) {
    if (c === 0) continue;
    const p = c / total;
    h -= p * Math.log2(p);
  }
  return h / 3; // max entropy for 8 bins is log2(8)=3
}

export function summarizeMouse(points: Array<{ x: number; y: number }>): MouseSummary {
  if (points.length < 2) return { moves: points.length, path_entropy: 0, distance: 0 };
  const angles: number[] = [];
  let distance = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    distance += Math.hypot(dx, dy);
    if (dx !== 0 || dy !== 0) angles.push(Math.atan2(dy, dx));
  }
  return { moves: points.length, path_entropy: directionEntropy(angles), distance };
}

export function summarizeTyping(flightTimes: number[], dwellTimes: number[]): TypingSummary {
  return {
    keys: dwellTimes.length,
    flight_var: variance(flightTimes),
    dwell_mean: mean(dwellTimes),
  };
}
